/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, type ReactNode } from 'react';

import { ChevronDown } from 'lucide-react';

import { Tooltip } from '../../primitives/Tooltip';
import { cn } from '../../utils';

export interface NavItemData {
  key: string;
  title: string;
  path?: string;
  icon?: ReactNode;
  info?: ReactNode;
  disabled?: boolean;
  /**
   * Atenúa visualmente el item (opacity). Por defecto sigue a `disabled`.
   * Pasar `false` con `disabled: true` muestra el item a color pleno pero sin
   * navegación (ej. producto visible "Próximamente" que aún no entra a su ruta).
   */
  dimmed?: boolean;
  children?: NavItemData[];
}

export interface NavGroupData {
  subheader?: string;
  items: NavItemData[];
}

export interface NavSectionProps {
  data: NavGroupData[];
  collapsed?: boolean;
  activeKey?: string;
  onItemClick?: (item: NavItemData) => void;
  renderLink?: (props: {
    item: NavItemData;
    children: ReactNode;
    className: string;
    onClick?: () => void;
  }) => ReactNode;
  className?: string;
}

interface NavItemProps {
  item: NavItemData;
  depth?: number;
  collapsed?: boolean;
  activeKey?: string;
  onItemClick?: (item: NavItemData) => void;
  renderLink?: NavSectionProps['renderLink'];
}

// Helper to check if nav item is active (supports child routes)
function checkIsActive(
  itemPath: string | undefined,
  itemKey: string,
  activeKey: string | undefined
): boolean {
  if (!activeKey) return false;
  // Match by key
  if (itemKey === activeKey) return true;
  // Match exact path
  if (itemPath === activeKey) return true;
  // Match child routes (activeKey starts with itemPath/)
  if (itemPath && activeKey.startsWith(itemPath + '/')) return true;
  return false;
}

function NavItem({ item, depth = 0, collapsed, activeKey, onItemClick, renderLink }: NavItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const isActive = checkIsActive(item.path, item.key, activeKey);

  const handleClick = () => {
    if (hasChildren) {
      setIsOpen(!isOpen);
    }
    onItemClick?.(item);
  };

  const itemContent = (
    <>
      {item.icon && (
        <span className={cn('flex-shrink-0', collapsed ? '' : 'mr-3')}>{item.icon}</span>
      )}
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.title}</span>
          {item.info && <span className="ml-2">{item.info}</span>}
          {hasChildren && (
            <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
          )}
        </>
      )}
    </>
  );

  // Active style mirrors the primary Button: bg-primary-500 + tkf-fg text.
  const itemClasses = cn(
    'flex items-center rounded-lg text-sm transition-colors w-full',
    'focus:outline-none focus-visible:bg-tkf-surface-muted',
    collapsed ? 'flex-col justify-center p-2.5' : 'gap-3 px-3 py-2.5',
    isActive
      ? 'bg-primary-500 font-bold text-tkf-fg hover:bg-primary-600'
      : 'font-medium text-tkf-fg hover:bg-tkf-surface-muted dark:hover:bg-white/5',
    item.disabled && 'cursor-not-allowed',
    (item.dimmed ?? item.disabled) && 'opacity-50',
    depth > 0 && !collapsed && 'pl-10'
  );

  const renderItem = () => {
    if (item.path && !hasChildren && renderLink) {
      return renderLink({
        item,
        children: itemContent,
        className: itemClasses,
        onClick: handleClick,
      });
    }

    if (item.path && !hasChildren) {
      return (
        <a
          href={item.disabled ? '#' : item.path}
          className={itemClasses}
          aria-current={isActive ? 'page' : undefined}
          onClick={handleClick}
        >
          {itemContent}
        </a>
      );
    }

    return (
      <button
        type="button"
        className={itemClasses}
        aria-current={isActive ? 'page' : undefined}
        onClick={handleClick}
        disabled={item.disabled}
      >
        {itemContent}
      </button>
    );
  };

  if (collapsed && item.icon) {
    return (
      <div>
        <Tooltip content={item.title} position="right" usePortal>
          {renderItem()}
        </Tooltip>
        {hasChildren && isOpen && !collapsed && (
          <div className="mt-1 space-y-1">
            {item.children?.map(child => (
              <NavItem
                key={child.key}
                item={child}
                depth={depth + 1}
                collapsed={collapsed}
                activeKey={activeKey}
                onItemClick={onItemClick}
                renderLink={renderLink}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {renderItem()}
      {hasChildren && isOpen && !collapsed && (
        <div className="mt-1 space-y-1">
          {item.children?.map(child => (
            <NavItem
              key={child.key}
              item={child}
              depth={depth + 1}
              collapsed={collapsed}
              activeKey={activeKey}
              onItemClick={onItemClick}
              renderLink={renderLink}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function NavSection({
  data,
  collapsed = false,
  activeKey,
  onItemClick,
  renderLink,
  className,
}: NavSectionProps) {
  return (
    <nav className={cn('space-y-2', className)}>
      {data.map((group, groupIndex) => (
        <div key={groupIndex} className="space-y-1">
          {group.subheader && !collapsed && (
            <h3 className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-tkf-fg-muted">
              {group.subheader}
            </h3>
          )}
          <div className="space-y-1">
            {group.items.map(item => (
              <NavItem
                key={item.key}
                item={item}
                collapsed={collapsed}
                activeKey={activeKey}
                onItemClick={onItemClick}
                renderLink={renderLink}
              />
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}
